import { isEmpty, map } from 'lodash/fp';
import { WithDataTestId } from '@mews-commander/types';
import { Target } from '@mews/enums';
import { Optional } from '@mews/std';
import {
    ExpandableList,
    ExpandableListItem,
    Icon,
    IconName,
    ListItem,
    ListItemProps,
    Stack,
    Tag,
} from '@optimus-web/core';
import LinkedListItem from './LinkedListItem';

export interface ListOption extends Pick<ListItemProps, 'primaryText'>, WithDataTestId {
    value: string;
    children?: Array<ListOption>;
    disabled?: boolean;
    iconName?: IconName;
    shouldUseValueAsLink?: boolean;
    tag?: string | null;
    target?: Target;
}

type ListOptions = Array<ListOption>;

export interface MenuProps {
    handleNavigation: (value: string) => void;
    options: ListOptions;
    multiSelect?: boolean;
    selected: Optional<string>;
}

const Menu: React.FC<MenuProps> = ({ options, multiSelect, handleNavigation, selected }) => {
    const handleItemClick: ListItemProps['onAfterClick'] = (_event, value) => {
        if (value) {
            handleNavigation(value as string);
        }
    };

    const mapOptions = map<ListOption, React.ReactNode>(
        ({
            value,
            primaryText,
            iconName,
            disabled,
            children,
            shouldUseValueAsLink = true,
            dataTestId,
            tag,
            target,
        }) => {
            const leftIcon = iconName ? <Icon value={iconName} variant="basic" /> : null;
            const commonProps = {
                key: value,
                primaryText:
                    tag == null ? (
                        primaryText
                    ) : (
                        <Stack spacing="100" itemAlignment="center">
                            <span>{primaryText}</span>
                            <Tag variant="primary">{tag}</Tag>
                        </Stack>
                    ),
                value,
                leftIcon,
                disabled,
                target,
                'data-test-id': dataTestId,
                dense: false,
            };
            const ListItemElement = shouldUseValueAsLink ? LinkedListItem : ListItem;

            return isEmpty(children) ? (
                <ListItemElement {...commonProps} focused={selected === value} onAfterClick={handleItemClick} />
            ) : (
                <ExpandableListItem {...commonProps} multiSelect={multiSelect}>
                    {mapOptions(children)}
                </ExpandableListItem>
            );
        }
    );

    return <ExpandableList multiSelect={multiSelect}>{mapOptions(options)}</ExpandableList>;
};

export default Menu;
